import * as React from 'react'
import { cx, tw } from 'utils/classnames'
import { CheckIcon } from 'primitives/icons'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { Typography } from '@repo/design-system-cf/components/typography'

import { BannerContainer } from './container'
import { useTranslations } from '../contexts/banner-config-context'

interface Props {
  banner: NonNullable<SiteDataQuery['siteData']['allAccessYearlyTrialBanner']>
  billing?: boolean
  className?: string
  takeover?: SiteDataQuery['siteData']['promo']['takeover']
}

interface CountdownTime {
  hours: number
  minutes: number
  seconds: number
  totalSeconds: number
  dateTime: string
}

function calculateTimeLeft(): CountdownTime {
  const now = new Date()
  const midnight = new Date(now)
  midnight.setHours(24, 0, 0, 0)

  const diff = midnight.getTime() - now.getTime()
  const totalSeconds = Math.max(0, Math.floor(diff / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const dateTime = `PT${hours}H${minutes}M${seconds}S`

  return { hours, minutes, seconds, totalSeconds, dateTime }
}

const defaultCountdown: CountdownTime = {
  hours: 23,
  minutes: 59,
  seconds: 59,
  totalSeconds: 86_399,
  dateTime: 'PT23H59M59S',
}

export function useCountdownToMidnight(): CountdownTime {
  const [timeLeft, setTimeLeft] = React.useState<CountdownTime>(defaultCountdown)
  const [hasMounted, setHasMounted] = React.useState(false)

  React.useEffect(() => {
    setHasMounted(true)
    setTimeLeft(calculateTimeLeft())

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Return default during SSR to avoid hydration mismatch
  if (!hasMounted) {
    return defaultCountdown
  }

  return timeLeft
}

export function CountdownSection({
  freeTodayText,
  hoursText,
  minutesText,
  secondsText,
}: {
  freeTodayText: string
  hoursText: string
  minutesText: string
  secondsText: string
}) {
  const { hours, minutes, seconds, dateTime } = useCountdownToMidnight()

  return (
    <div className="flex flex-col items-center mb-2">
      <p className="text-11">{freeTodayText}</p>
      <time
        className="text-30 font-bold text-cf-red-800 flex gap-2 tabular-nums"
        dateTime={dateTime}
        aria-live="off"
      >
        <span className="flex flex-col items-center w-[2ch]">
          <span>{String(hours).padStart(2, '0')}</span>
          <span className="uppercase text-11 text-cf-grey-600">{hoursText}</span>
        </span>
        <span>:</span>
        <span className="flex flex-col items-center w-[2ch]">
          <span>{String(minutes).padStart(2, '0')}</span>
          <span className="uppercase text-11 text-cf-grey-600">{minutesText}</span>
        </span>
        <span>:</span>
        <span className="flex flex-col items-center w-[2ch]">
          <span>{String(seconds).padStart(2, '0')}</span>
          <span className="uppercase text-11 text-cf-grey-600">{secondsText}</span>
        </span>
      </time>
    </div>
  )
}

export function AllAccessYearlyTrial({ banner, className, takeover }: Props) {
  const t = useTranslations()

  const { features, headline, buttonText } = banner

  function onButtonClick() {
    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  let textColor = ''
  let backgroundImage: string | null = banner.backgroundImage
  let buttonColor = ''
  let buttonTextColor = ''
  const buttonStyles = cx(
    'bg-secondary-500 hover:bg-secondary-700 text-cf-grey-0 h-[45px] leading-[45px] w-full items-center justify-center rounded py-5 px-2 text-[1rem] font-bold uppercase tracking-[0.9px] leading-[18px]',
  )

  if (takeover?.isActive) {
    ;({
      aboveNavBannerColor: textColor,
      aboveNavBannerImg: backgroundImage,
      aboveNavBannerBtnColor: buttonColor,
      aboveNavBannerBtnTextColor: buttonTextColor,
    } = takeover)
  }

  const isDailyGiftsPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)

  const entryPoint = isDailyGiftsPage
    ? PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
    : PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV

  return (
    <BannerContainer
      className={className}
      backgroundImage={backgroundImage}
      wrapperClassName="items-start"
    >
      <div className="w-full">
        <div
          className="mb-[10px] text-center text-[12px] leading-7 tracking-[0.3px] text-[#2A1F3F] md:text-left md:text-[16px] [&_.u-bold]:text-[16px] [&_.u-bold]:font-bold md:[&_.u-bold]:text-[24px]"
          dangerouslySetInnerHTML={{ __html: headline }}
          style={{ color: textColor }}
        />
        <StartFreeTrialButton
          style={{
            color: buttonTextColor,
            backgroundColor: buttonColor,
          }}
          onClick={onButtonClick}
          entryPoint={entryPoint}
          className={tw(buttonStyles, 'flex md:hidden')}
          label={buttonText}
        />
        {features ? (
          <ul className="mt-3.5 hidden md:block">
            {features.map((feat, index) => {
              return (
                <li
                  key={index}
                  className={tw(
                    'my-1 flex items-center leading-[1.618]',
                    index > 0 && 'hidden md:flex',
                  )}
                  style={{ color: textColor }}
                >
                  <CheckIcon className="mr-1.5 text-green-500" style={{ color: textColor }} />
                  <span dangerouslySetInnerHTML={{ __html: feat }} />
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>
      <div className={tw('hidden w-[41%] flex-col items-center md:flex')}>
        <CountdownSection
          freeTodayText={t('try_free_today')}
          hoursText={t('hours')}
          minutesText={t('mins')}
          secondsText={t('secs')}
        />
        <StartFreeTrialButton
          style={{
            color: buttonTextColor,
            backgroundColor: buttonColor,
          }}
          onClick={onButtonClick}
          entryPoint={entryPoint}
          className={tw(buttonStyles, 'hidden md:flex')}
          label={buttonText}
        />
        <Typography
          variant="cf-label-02"
          className="mt-2 text-semantic-foreground-color-subtle"
          style={{ color: textColor }}
        >
          {t('cancel_anytime')}
        </Typography>
      </div>
    </BannerContainer>
  )
}
